import { useState, useMemo } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";

/**
 * Reglas de unidades permitidas por tipo de cálculo
 */
const UNITS_BY_CALC_KIND = {
  INCREMENT_PER_UNIT: ["meter", "second"],
  ONE_TIME_SURCHARGE: ["service"],
  HOURLY: ["hour"],
  MINIMUM_FARE: ["flat"],
};

export default function ConceptModal({ show, onClose, onCreate, onSuccess }) {
  const [form, setForm] = useState({
    name: "",
    calc_kind: "INCREMENT_PER_UNIT",
    unit: "",
    unit_qty: "",
  });

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  /* ===============================
     Unidades permitidas dinámicas
     =============================== */
  const allowedUnits = useMemo(() => {
    return UNITS_BY_CALC_KIND[form.calc_kind] ?? [];
  }, [form.calc_kind]);

  const requiresUnitQty = form.calc_kind === "INCREMENT_PER_UNIT";

  /* ===============================
     Handlers
     =============================== */
  const setField = (patch) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const handleSave = async () => {
    setSaving(true);
    setErrors({});

    try {
      const payload = {
        name: form.name,
        calc_kind: form.calc_kind,
        unit: form.unit,
        unit_qty:
          form.calc_kind === "INCREMENT_PER_UNIT"
            ? Number(form.unit_qty)
            : null,
      };

      const created = await onCreate(payload);

      // ✅ notificar éxito al padre
      onSuccess(created);
      setForm({
        name: "",
        calc_kind: "INCREMENT_PER_UNIT",
        unit: "",
        unit_qty: "",
      });
    } catch (err) {
      if (err.response?.status === 422) {
        setErrors(err.response.data.errors || {});
      } else {
        setErrors({
          general: ["No fue posible guardar el concepto."],
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const isValid =
    form.name &&
    form.calc_kind &&
    form.unit &&
    (!requiresUnitQty || form.unit_qty > 0);

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Nuevo concepto tarifario</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {errors.general && (
          <Alert variant="danger">{errors.general[0]}</Alert>
        )}
        <Form>

          {/* ===============================
              Nombre
             =============================== */}
          <Form.Group className="mb-3">
            <Form.Label>Nombre del concepto</Form.Label>
            <Form.Control
              placeholder="Ej: Recargo nocturno"
              value={form.name}
              onChange={(e) => setField({ name: e.target.value })}
              isInvalid={!!errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name?.[0]}
            </Form.Control.Feedback>
          </Form.Group>

          {/* ===============================
              Tipo de cálculo
             =============================== */}
          <Form.Group className="mb-3">
            <Form.Label>Tipo de cálculo</Form.Label>
            <Form.Select
              value={form.calc_kind}
              onChange={(e) =>
                setField({
                  calc_kind: e.target.value,
                  unit: "",
                  unit_qty: "",
                })
              }
              isInvalid={!!errors.calc_kind}
            >
              <option value="INCREMENT_PER_UNIT">
                Incremento por unidad
              </option>
              <option value="ONE_TIME_SURCHARGE">
                Recargo único
              </option>
              <option value="HOURLY">
                Cobro por hora
              </option>
              <option value="MINIMUM_FARE">
                Valor mínimo
              </option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.calc_kind?.[0]}
            </Form.Control.Feedback>
          </Form.Group>

          {/* ===============================
              Unidad de medida (guiada)
             =============================== */}
          <Form.Group className="mb-3">
            <Form.Label>Unidad de medida</Form.Label>
            <Form.Select
              value={form.unit}
              onChange={(e) => setField({ unit: e.target.value })}
              isInvalid={!!errors.unit}
              disabled={allowedUnits.length === 0}
            >
              <option value="">Seleccione…</option>

              {allowedUnits.includes("meter") && (
                <option value="meter">Metros</option>
              )}
              {allowedUnits.includes("second") && (
                <option value="second">Segundos</option>
              )}
              {allowedUnits.includes("service") && (
                <option value="service">Servicio</option>
              )}
              {allowedUnits.includes("hour") && (
                <option value="hour">Hora</option>
              )}
              {allowedUnits.includes("flat") && (
                <option value="flat">Valor fijo</option>
              )}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.unit?.[0]}
            </Form.Control.Feedback>
          </Form.Group>

          {/* ===============================
              Cantidad por unidad (solo incremento)
             =============================== */}
          {requiresUnitQty && (
            <Form.Group className="mb-3">
              <Form.Label>Cantidad por unidad</Form.Label>
              <Form.Control
                type="number"
                min={1}
                step={1}
                placeholder="Ej: 80"
                value={form.unit_qty}
                onChange={(e) =>
                  setField({ unit_qty: e.target.value })
                }
                isInvalid={!!errors.unit_qty}
              />
              <Form.Text className="text-muted">
                Ejemplo: cada 80 metros o cada 60 segundos.
              </Form.Text>
              <Form.Control.Feedback type="invalid">
                {errors.unit_qty?.[0]}
              </Form.Control.Feedback>
            </Form.Group>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="primary"
          disabled={!isValid || saving}
          onClick={handleSave}
        >
          {saving ? (
            <>
              <Spinner size="sm" className="me-2" />
              Guardando…
            </>
          ) : (
            "Guardar concepto"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
